import React, { useCallback } from 'react';
import { SecretWidgetState } from '@kemu-io/kemu-core/types';
import Icon from '@ant-design/icons';
import {
  InputName,
  OutputName,
	getDefaultState,
} from '@kemu-io/kemu-core/widgets/secret/index.js';
import { DataType } from '@kemu-io/hs-types';
import {
  GateUI,
	GateUIProps,
  GetPortsInformationFunction
} from '../index.js';
import styles from './secret.module.css';
import TextInput from '@components/WidgetsComponents/TextInput/TextInput';
import useReactiveWidgetState from '@hooks/useReactiveWidgetState';
import GateIcon from '@components/gateIcon/gateIcon';
import useTranslation from '@hooks/useTranslation';
import { ReactComponent as SecretWidgetIcon } from '@src/assets/img/gates/secret-widget.svg';
import { recipeCache } from '@kemu-io/kemu-core/common';
import { useDebouncedCallback } from 'use-debounce';
import { useDispatch } from 'react-redux';
import { resetWidgetInstance } from '@src/features/LogicMapper/logicMapperSlice';

type Props = GateUIProps

const updateSecretName = (config: {
	recipeId: string;
	widgetId: string;
	thingRecipeId: string;
	secretName: string;
}) => {
	const { recipeId, widgetId, thingRecipeId, secretName } = config;

	const thisWidget = recipeCache.findWidgetInRecipe(recipeId, thingRecipeId, widgetId);
	if (thisWidget) {
		thisWidget.secrets = {
			[secretName]: { name: secretName },
		};
	}
};


const SecretWidget = (props: Props) => {
  const { recipeId, thingRecipeId, info } = props;
  const [state, setState] = useReactiveWidgetState<SecretWidgetState>(recipeId, thingRecipeId, info.id);
  const t = useTranslation('Widget.Secret');
	const dispatch = useDispatch();

	const fixedState = {
		...getDefaultState(),
		...state
	};

	// Update the redux state for this widget to force showing the mapping icon in the main menu
	const resetWidgetInstanceDebounced = useDebouncedCallback(() => {
		dispatch(resetWidgetInstance({ widgetId: info.id }));
	}, 1000);

  const updateName = useCallback((name: string) => {    
		const upperCasedName = name.toUpperCase().replace(/\s+/g, '_');
    setState((s) => ({
      ...s,
      name: upperCasedName,
    }));

		updateSecretName({
			recipeId,
			widgetId: info.id,
			thingRecipeId,
			secretName: upperCasedName,
		});

		resetWidgetInstanceDebounced();
  }, [dispatch, info.id, resetWidgetInstanceDebounced]);

  return (
    <div className={styles.InputWrapper}>
      <TextInput
        value={fixedState.name.toUpperCase()}
        placeholder={t('Placeholder')}
        onChange={(e) => updateName(e.currentTarget.value)}
      />
    </div>
  );
};

/** Icon to be added to the bar */
const GateBarIcon = (): React.JSX.Element => {
	return (
		<GateIcon icon={<Icon component={SecretWidgetIcon} />}/>
	);
};


const getPortsInformation: GetPortsInformationFunction = (state, widgetInfo) => {
	return {
		inputs: [{
      name: InputName,
      type: DataType.Anything,
      position: 'Left',
    }],
		outputs: [{
      name: OutputName,
      type: DataType.String,
      position: 'Right',
    }],
	};
};

export default {
	getPortsInformation,
  hasTitle: true,

	BarIcon: GateBarIcon,
	Element: SecretWidget,
	getGatesBarTitle: (intl) => intl.formatMessage({ id: 'Widget.Secret.Title' }),
} as GateUI;
